import React, { useMemo, useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card } from '../components/Card';
import { Briefcase, Search, Save, FilePlus2, Tag, Filter, X } from 'lucide-react';
import { Input } from '../components/Input';
import { Textarea } from '../components/Textarea';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Label } from '../components/Label';
import { Checkbox } from '../components/Checkbox';
import { toast } from 'sonner';
const results = [{
  id: 1,
  src: '@budis_pengamat • X',
  time: '15:42 WIB',
  text: 'Saya kira pemerintah perlu evaluasi mekanisme penyaluran bansos, terlalu banyak laporan distribusi tidak merata...',
  sent: 'NEG' as const,
  platform: 'X',
  region: 'Jawa'
}, {
  id: 2,
  src: '@warga_demak • TikTok',
  time: '15:38 WIB',
  text: 'Bantuan untuk korban banjir Demak datang cepat, harapan kami terus berkelanjutan...',
  sent: 'POS' as const,
  platform: 'TikTok',
  region: 'Jawa'
}, {
  id: 3,
  src: '@infodaerah_up • IG',
  time: '15:31 WIB',
  text: 'INFO PENTING: bansos pangan dikabarkan dihentikan? Cek kebenaran ke sumber resmi.',
  sent: 'NEU' as const,
  platform: 'Instagram',
  region: 'Nasional'
}, {
  id: 4,
  src: '@kritik_pedas99 • X',
  time: '15:22 WIB',
  text: 'Pajak hiburan 75% benar-benar membunuh industri di Bali, banyak teman saya kena PHK...',
  sent: 'NEG' as const,
  platform: 'X',
  region: 'Bali-Nusra'
}, {
  id: 5,
  src: '@sukmaa.n • TikTok',
  time: '15:15 WIB',
  text: 'MBG di sekolah anak saya bagus banget, terima kasih pak Presiden 🙏',
  sent: 'POS' as const,
  platform: 'TikTok',
  region: 'Jawa'
}, {
  id: 6,
  src: '@tanisejahtera_id • X',
  time: '15:09 WIB',
  text: 'Pupuk subsidi mulai sulit di gudang kami, mohon Kementan koordinasi dengan distributor...',
  sent: 'NEU' as const,
  platform: 'X',
  region: 'Sumatera'
}];
const ALL_PLATFORMS = ['X', 'TikTok', 'Facebook', 'Instagram', 'YouTube'];
const ALL_SENTIMENTS = ['Positif', 'Netral', 'Negatif'];
const ALL_REGIONS = ['Nasional', 'Jawa', 'Sumatera', 'Kalimantan', 'Sulawesi', 'Bali-Nusra', 'Papua'];
const sentMap: Record<string, string> = {
  Positif: 'POS',
  Netral: 'NEU',
  Negatif: 'NEG'
};
export function Workbench() {
  const [query, setQuery] = useState('bansos pangan');
  const [platforms, setPlatforms] = useState<string[]>(['X', 'TikTok']);
  const [sentiments, setSentiments] = useState<string[]>(['Positif', 'Netral', 'Negatif']);
  const [regions, setRegions] = useState<string[]>(['Nasional']);
  const [notes, setNotes] = useState('Catatan analis:\n- Hoaks distribusi tidak merata terdeteksi di WhatsApp Channels\n- Narasi terkoordinasi #BansosTidakAdil oleh 1.247 akun\n- Rekomendasi: Kemensos rilis data resmi 8 jam');
  const [pinnedIds, setPinnedIds] = useState<number[]>([1, 2, 3]);
  const [isSaving, setIsSaving] = useState(false);
  const toggleArr = (arr: string[], set: (v: string[]) => void, val: string) => {
    if (arr.includes(val)) set(arr.filter(x => x !== val));else set([...arr, val]);
  };
  const filteredResults = useMemo(() => {
    return results.filter(r => {
      const q = query.toLowerCase();
      if (q && !r.text.toLowerCase().includes(q) && !r.src.toLowerCase().includes(q)) return false;
      if (platforms.length && !platforms.includes(r.platform)) return false;
      if (sentiments.length && !sentiments.map(s => sentMap[s]).includes(r.sent)) return false;
      if (regions.length && !regions.includes(r.region) && !regions.includes('Nasional')) return false;
      return true;
    });
  }, [query, platforms, sentiments, regions]);
  const activeFilterCount = (platforms.length < ALL_PLATFORMS.length ? 1 : 0) + (sentiments.length < ALL_SENTIMENTS.length ? 1 : 0) + (regions.length > 1 || !regions.includes('Nasional') ? 1 : 0);
  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      toast.success('Catatan investigasi berhasil disimpan');
    }, 800);
  };
  const pinned = results.filter(r => pinnedIds.includes(r.id));
  const togglePin = (id: number) => {
    if (pinnedIds.includes(id)) {
      setPinnedIds(pinnedIds.filter(x => x !== id));
      toast.info('Bukti dilepas dari pin');
    } else {
      setPinnedIds([...pinnedIds, id]);
      toast.success('Bukti ditambahkan ke pin');
    }
  };
  const resetFilters = () => {
    setPlatforms([...ALL_PLATFORMS]);
    setSentiments([...ALL_SENTIMENTS]);
    setRegions(['Nasional']);
    toast.info('Filter direset');
  };
  return <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6" data-id="element-3789">
      <PageHeader group="Workspace" title="Analyst Workbench" description="Ruang kerja analis: pencarian lintas-sumber, koleksi bukti, dan catatan investigasi." icon={Briefcase} data-id="element-3790" />

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-[700px] lg:h-[700px]" data-id="element-3791">
        {/* LEFT — Filters */}
        <Card className="col-span-12 lg:col-span-3 bg-card border-border/50 flex flex-col overflow-hidden" data-id="element-3792">
          <div className="p-3 border-b border-border/50 bg-muted/10 flex items-center justify-between" data-id="element-3793">
            <h3 className="font-mono text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5" data-id="element-3794">
              <Filter className="size-3.5" data-id="element-3795" />
              Filter Pencarian
            </h3>
            {activeFilterCount > 0 && <button onClick={resetFilters} className="font-mono text-[9px] uppercase text-muted-foreground hover:text-foreground transition flex items-center gap-1" data-id="element-3796">
                <X className="size-3" data-id="element-3797" />
                Reset
              </button>}
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-4 scrollbar-thin scrollbar-thumb-border" data-id="element-3798">
            <div data-id="element-3799">
              <Label className="text-xs font-mono uppercase text-muted-foreground" data-id="element-3800">
                Query
              </Label>
              <div className="relative mt-1.5" data-id="element-3801">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" data-id="element-3802" />
                <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Kata kunci..." className="pl-7 bg-muted/30 border-border/50 h-8 text-xs" data-id="element-3803" />
              </div>
            </div>

            <div data-id="element-3804">
              <Label className="text-xs font-mono uppercase text-muted-foreground" data-id="element-3805">
                Platform
              </Label>
              <div className="mt-1.5 space-y-1.5" data-id="element-3806">
                {ALL_PLATFORMS.map(p => <label key={p} className="flex items-center gap-2 text-xs cursor-pointer hover:text-primary transition" data-id="element-3807">
                    <Checkbox checked={platforms.includes(p)} onCheckedChange={() => toggleArr(platforms, setPlatforms, p)} data-id="element-3808" />
                    {p}
                  </label>)}
              </div>
            </div>

            <div data-id="element-3809">
              <Label className="text-xs font-mono uppercase text-muted-foreground" data-id="element-3810">
                Sentimen
              </Label>
              <div className="mt-1.5 space-y-1.5" data-id="element-3811">
                {ALL_SENTIMENTS.map(s => <label key={s} className="flex items-center gap-2 text-xs cursor-pointer hover:text-primary transition" data-id="element-3812">
                    <Checkbox checked={sentiments.includes(s)} onCheckedChange={() => toggleArr(sentiments, setSentiments, s)} data-id="element-3813" />
                    {s}
                  </label>)}
              </div>
            </div>

            <div data-id="element-3814">
              <Label className="text-xs font-mono uppercase text-muted-foreground" data-id="element-3815">
                Wilayah
              </Label>
              <div className="mt-1.5 space-y-1.5" data-id="element-3816">
                {ALL_REGIONS.map(r => <label key={r} className="flex items-center gap-2 text-xs cursor-pointer hover:text-primary transition" data-id="element-3817">
                    <Checkbox checked={regions.includes(r)} onCheckedChange={() => toggleArr(regions, setRegions, r)} data-id="element-3818" />
                    {r}
                  </label>)}
              </div>
            </div>
          </div>

          <div className="p-3 border-t border-border/50 bg-muted/10" data-id="element-3819">
            <Button size="sm" className="w-full" onClick={() => toast.success(`Pencarian dijalankan · ${filteredResults.length} hasil`)} data-id="element-3820">
              <Search className="size-3.5 mr-1.5" data-id="element-3821" />
              Cari (Ctrl+Enter)
            </Button>
          </div>
        </Card>

        {/* MIDDLE — Results */}
        <Card className="col-span-12 lg:col-span-5 bg-card border-border/50 flex flex-col overflow-hidden" data-id="element-3822">
          <div className="p-3 border-b border-border/50 flex items-center justify-between bg-muted/10" data-id="element-3823">
            <h3 className="font-mono text-xs uppercase tracking-wider text-muted-foreground" data-id="element-3824">
              Hasil Pencarian
            </h3>
            <Badge variant="outline" className="font-mono text-[9px]" data-id="element-3825">
              {filteredResults.length} Ditemukan
            </Badge>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3 scrollbar-thin scrollbar-thumb-border" data-id="element-3826">
            {filteredResults.length > 0 ? filteredResults.map(r => {
            const isPinned = pinnedIds.includes(r.id);
            return <div key={r.id} className={`group p-3 rounded-lg border transition cursor-pointer ${isPinned ? 'border-primary/40 bg-primary/5' : 'border-border/50 bg-muted/10 hover:bg-muted/20'}`} data-id="element-3827">
                    <div className="flex items-center justify-between mb-1" data-id="element-3828">
                      <span className="font-mono text-[10px] text-muted-foreground" data-id="element-3829">
                        {r.src} • {r.time}
                      </span>
                      <div className="flex items-center gap-2" data-id="element-3830">
                        <span className="font-mono text-[9px] text-muted-foreground" data-id="element-3831">
                          {r.region}
                        </span>
                        <span className={`font-mono text-[9px] font-bold ${r.sent === 'POS' ? 'text-emerald-600' : r.sent === 'NEG' ? 'text-red-600' : 'text-muted-foreground'}`} data-id="element-3832">
                          {r.sent}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs leading-relaxed" data-id="element-3833">{r.text}</p>
                    <div className="flex gap-2 mt-2 opacity-0 group-hover:opacity-100 transition" data-id="element-3834">
                      <Button size="xs" variant={isPinned ? 'default' : 'ghost'} className="h-6 px-2 text-[10px]" onClick={() => togglePin(r.id)} data-id="element-3835">
                        <FilePlus2 className="size-3 mr-1" data-id="element-3836" />
                        {isPinned ? 'Tersimpan' : 'Pin Bukti'}
                      </Button>
                      <Button size="xs" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => toast.success('Tag ditambahkan')} data-id="element-3837">
                        <Tag className="size-3 mr-1" data-id="element-3838" />
                        Tag
                      </Button>
                    </div>
                  </div>;
          }) : <div className="h-full flex flex-col items-center justify-center text-muted-foreground opacity-60 py-12" data-id="element-3839">
                <Search className="size-10 mb-2" data-id="element-3840" />
                <p className="text-sm" data-id="element-3841">Tidak ada hasil ditemukan</p>
                <p className="text-xs mt-1" data-id="element-3842">
                  Ubah query atau filter di panel kiri.
                </p>
              </div>}
          </div>
        </Card>

        {/* RIGHT — Notes & Evidence */}
        <Card className="col-span-12 lg:col-span-4 bg-card border-border/50 flex flex-col overflow-hidden" data-id="element-3843">
          <div className="p-3 border-b border-border/50 bg-muted/10 flex items-center justify-between" data-id="element-3844">
            <h3 className="font-mono text-xs uppercase tracking-wider text-muted-foreground" data-id="element-3845">
              Catatan & Bukti
            </h3>
            <Badge variant="outline" className="font-mono text-[9px]" data-id="element-3846">
              {pinned.length} Bukti Ter-pin
            </Badge>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-4 scrollbar-thin scrollbar-thumb-border" data-id="element-3847">
            <div data-id="element-3848">
              <Label className="text-xs font-mono uppercase text-muted-foreground mb-1.5 block" data-id="element-3849">
                Catatan Analis
              </Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Tulis catatan investigasi..." className="bg-muted/20 border-border/50 text-xs font-mono min-h-[200px] resize-y" data-id="element-3850" />
            </div>

            <div data-id="element-3851">
              <p className="font-mono text-[10px] uppercase text-muted-foreground mb-2" data-id="element-3852">
                Bukti Ter-pin ({pinned.length})
              </p>
              {pinned.length > 0 ? <div className="space-y-1.5" data-id="element-3853">
                  {pinned.map(r => <div key={r.id} className="p-2 rounded border border-primary/30 bg-primary/5 text-[10px] group/p" data-id="element-3854">
                      <div className="flex items-start justify-between gap-2" data-id="element-3855">
                        <p className="font-mono text-muted-foreground truncate flex-1" data-id="element-3856">
                          {r.src}
                        </p>
                        <button onClick={() => togglePin(r.id)} className="opacity-0 group-hover/p:opacity-100 transition text-muted-foreground hover:text-red-500" title="Lepas pin" data-id="element-3857">
                          <X className="size-3" data-id="element-3858" />
                        </button>
                      </div>
                      <p className="line-clamp-2 mt-0.5" data-id="element-3859">{r.text}</p>
                    </div>)}
                </div> : <div className="p-3 rounded border border-dashed border-border/50 text-[10px] text-muted-foreground text-center" data-id="element-3860">
                  Belum ada bukti ter-pin. Hover hasil pencarian dan klik
                  &quot;Pin Bukti&quot;.
                </div>}
            </div>
          </div>

          <div className="p-3 border-t border-border/50 bg-muted/10 flex justify-end gap-2" data-id="element-3861">
            <Button variant="outline" size="sm" onClick={() => toast.info('Membuka file picker...')} data-id="element-3862">
              <FilePlus2 className="size-3.5 mr-1.5" data-id="element-3863" />
              Lampirkan
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isSaving} data-id="element-3864">
              <Save className="size-3.5 mr-1.5" data-id="element-3865" />
              {isSaving ? 'Menyimpan...' : 'Simpan Catatan'}
            </Button>
          </div>
        </Card>
      </div>
    </div>;
}